/**
 * La Vague (GDD section 8). A wave 32 cells high (the height of 2 units) leaves the unit in the aimed
 * direction and runs along the ground. It loses one cell of height every 12 cells, leaves a trail of water
 * behind, and stops against a wall higher than itself or when it is worn out (after about 384 cells).
 * It sweeps the units (10 damage at the impact), the powders (sand) and the mines along, and sets lava into
 * obsidian; the powders fall where it wears out, not against the wall that stops it (author's rule). Synergies (GDD): it freezes into a wall of ice on an ice mine or under a cryo-grenade, carries a
 * discharge through its trail over Robotman's electric carcass, and burns all the way with burning oil on it.
 */
import { Physique, STATE_LIQUID, STATE_POWDER, STATE_STATIC, wakeRect } from '@invaders/sim';
import { FP, FP_SHIFT, TICKS_PER_SECOND, type GameState, type Surge, type Unit } from './state';
import { UNIT_H, UNIT_W, damageUnit, ownerPlayer, unitCenter } from './unit';
import { WEAPONS } from './weapons';
import { WEAPON_WAVE } from './weapon-ids';
import { MINE_ICE } from './mines';
import { discharge } from './cold';
import { ROBOTMAN } from './invaders';
import { LEGACY_TURNS, DISCHARGE_DAMAGE } from './legacy';

const { EMPTY, WATER, LAVA, OBSIDIAN, SAND, ICE, FIRE, OIL } = Physique;

/** Rows of height a Roomba swallows per tick: half a second for a whole wave. */
const SURGE_EATEN = 1;

export const SURGE_HEIGHT = 32;
/** One cell of height lost every this many cells. */
export const SURGE_DECAY = 12;
/** Columns per tick. */
export const SURGE_SPEED = 2;
/** Thickness of the trail of water left on the ground. */
export const SURGE_TRAIL = 2;
/** Width of the body of the wave behind its front: powders falling in there are swept too. */
export const SURGE_BODY = 8;
/** Columns a Wave runs in 2 s: on the Moon it freezes there. */
export const SURGE_AIRLESS_COLUMNS = 2 * TICKS_PER_SECOND * SURGE_SPEED;
/** Units riding just above the crest count as in the wave (it lifts what it carries). */
const CREST_MARGIN = 6;

/** A liquid at least this deep carries the wave on its surface (its own trail, 2 cells, does not). */
const SURGE_RIDE_DEPTH = 4;

/**
 * What the wave runs on in column x, from row `from` down: the first static cell, or the surface of a liquid other
 * than lava at least SURGE_RIDE_DEPTH deep (the author's rule: the Wave stays at the height of the water instead of
 * diving to its bottom; it still goes through lava, which it sets into obsidian). h if none.
 */
function ground(state: GameState, x: number, from: number): number {
  const { w, h, data } = state.world.physique;
  const matState = state.world.rules.materials.state;
  for (let y = Math.max(0, from); y < h; y++) {
    const m = data[y * w + x];
    if (matState[m] === STATE_STATIC) return y;
    if (matState[m] === STATE_LIQUID && m !== LAVA) {
      let depth = 1;
      while (depth < SURGE_RIDE_DEPTH && y + depth < h && matState[data[(y + depth) * w + x]] === STATE_LIQUID) depth++;
      if (depth >= SURGE_RIDE_DEPTH) return y;
    }
  }
  return h;
}

export function launchSurge(state: GameState, unit: Unit, dir: 1 | -1): void {
  const [cx] = unitCenter(unit);
  const x = cx + dir * (UNIT_W >> 1);
  const floor = ground(state, Math.min(state.world.physique.w - 1, Math.max(0, cx)), (unit.y >> FP_SHIFT) + UNIT_H - 2);
  state.surges.push({ x, dir, floor, height: WEAPONS[WEAPON_WAVE].radius, travelled: 0, owner: unit.id, carried: 0, hit: [unit.id], electric: false, burning: false });
}

/** Free cells for dropped matter: empty or gas, outside units. */
function freeCell(state: GameState, x: number, y: number): boolean {
  const { w, h, data } = state.world.physique;
  if (x < 0 || x >= w || y < 0 || y >= h) return false;
  // Only empty or gas cells (state > liquid).
  if (data[y * w + x] !== EMPTY && state.world.rules.materials.state[data[y * w + x]] <= STATE_LIQUID) return false;
  for (const u of state.units) {
    if (!u.alive) continue;
    const ux = u.x >> FP_SHIFT;
    const uy = u.y >> FP_SHIFT;
    if (x >= ux && x < ux + UNIT_W && y >= uy && y < uy + UNIT_H) return false;
  }
  return true;
}

/** Drops `n` cells of `mat` around column x, from the row above `floor` upward, in free cells. */
function drop(state: GameState, x: number, floor: number, n: number, mat: number, spread: number): void {
  const { w } = state.world.physique;
  let left = n;
  for (let y = floor - 1; y >= 0 && left > 0; y--) {
    for (let dx = 0; dx <= spread && left > 0; dx++) {
      for (const xx of dx === 0 ? [x] : [x - dx, x + dx]) {
        if (left > 0 && freeCell(state, xx, y)) { state.world.physique.data[y * w + xx] = mat; state.world.cellState.data[y * w + xx] = 0; left--; }
      }
    }
  }
  wakeRect(state.world, x - spread, floor - 40, x + spread, floor);
}

function breakSurge(state: GameState, s: Surge): void {
  // The wave collapses and its water spills. Worn out, it lets what it carries fall at its front; stopped against a
  // wall (or the edge of the map), it drops no sand (author's rule: no heap of sand rising against the wall).
  if (s.carried > 0 && s.height <= 0) drop(state, s.x - s.dir, s.floor, s.carried, SAND, 6);
  drop(state, s.x - s.dir, s.floor, s.height * 6, WATER, 8);
  state.events.push({ type: 'surge', x: s.x, y: s.floor, height: s.height });
}

/**
 * Synergies "Mur de glace" and "Vague figée": the wave sets at once into a wall of ice, at the height it had
 * there, over its body. It is then gone (no water spills).
 */
export function freezeSurge(state: GameState, s: Surge): void {
  const { w, data } = state.world.physique;
  const top = s.floor - s.height;
  for (let k = 0; k < SURGE_BODY; k++) {
    const x = s.x - k * s.dir;
    if (x < 0 || x >= w) break;
    const g = ground(state, x, top);
    for (let y = Math.max(0, g - s.height); y < g; y++) {
      if (freeCell(state, x, y) || data[y * w + x] === WATER) { data[y * w + x] = ICE; state.world.cellState.data[y * w + x] = 0; }
    }
  }
  wakeRect(state.world, s.x - SURGE_BODY - 1, top - 1, s.x + SURGE_BODY + 1, s.floor + 1);
  state.events.push({ type: 'freeze', x: s.x, y: s.floor - (s.height >> 1), radius: s.height >> 1 });
  const i = state.surges.indexOf(s);
  if (i >= 0) state.surges.splice(i, 1);
}

/** One column of advance. Returns false when the wave stops. */
function advance(state: GameState, s: Surge): boolean {
  const world = state.world;
  const { w, data } = world.physique;
  const cells = world.cellState.data;
  const matState = world.rules.materials.state;
  const nx = s.x + s.dir;
  if (nx < 0 || nx >= w) return false;
  const top = s.floor - s.height;
  const floor = ground(state, nx, top);
  // A wall higher than the wave stops it (it climbs smaller steps and runs down slopes).
  if (floor <= top) return false;
  // Riding on a sheet of oil, it carries it as if the oil were in its body (Raz-de-feu).
  if (floor < state.world.physique.h && data[floor * w + nx] === OIL) s.burning = true;

  // The body: the front column and the ones just behind it.
  for (let k = 0; k < SURGE_BODY; k++) {
    const x = nx - k * s.dir;
    if (x < 0 || x >= w) break;
    const g = k === 0 ? floor : ground(state, x, top);
    for (let y = Math.max(0, g - s.height); y < g; y++) {
      const i = y * w + x;
      const m = data[i];
      if (matState[m] === STATE_POWDER) { data[i] = EMPTY; cells[i] = 0; s.carried++; }
      else if (m === LAVA) { data[i] = OBSIDIAN; cells[i] = 0; }
      else if (m === FIRE || m === OIL) s.burning = true; // burning oil rides on the wave
    }
  }
  // Trail of water on the ground at the back of the body.
  const back = nx - SURGE_BODY * s.dir;
  if (back >= 0 && back < w) {
    const g = ground(state, back, top);
    for (let y = g - 1; y >= g - SURGE_TRAIL && y >= 0; y--) {
      if (freeCell(state, back, y)) { data[y * w + back] = WATER; cells[y * w + back] = 0; }
    }
  }
  wakeRect(world, Math.min(nx, back) - 1, floor - s.height - 1, Math.max(nx, back) + 1, Math.max(floor, s.floor) + 1);

  // Raz-de-feu: the burning wave sets fire to what it touches, along its crest.
  if (s.burning) {
    for (let y = Math.max(0, floor - s.height); y < Math.max(0, floor - s.height) + 2; y++) {
      if (freeCell(state, nx, y)) { data[y * w + nx] = FIRE; cells[y * w + nx] = 0; }
    }
  }
  // Units and mines in the wave are swept along.
  for (const u of state.units) {
    if (!u.alive) continue;
    const ux = u.x >> FP_SHIFT;
    const uy = u.y >> FP_SHIFT;
    if (nx < ux || nx >= ux + UNIT_W || uy + UNIT_H <= floor - s.height - CREST_MARGIN || uy >= floor) continue;
    if (!s.hit.includes(u.id)) { s.hit.push(u.id); damageUnit(state, u, WEAPONS[WEAPON_WAVE].damage, 'direct'); }
    if (s.burning) u.burning = true;
    u.vx = s.dir * SURGE_SPEED * FP;
    u.vy = -(FP >> 1);
    u.walkAcc = 0;
  }
  for (const m of state.mines) {
    if (Math.abs(m.x - nx) <= 1 && m.y >= floor - s.height && m.y < floor + 2) {
      if (m.kind === MINE_ICE) { s.x = nx; s.floor = floor; state.mines.splice(state.mines.indexOf(m), 1); freezeSurge(state, s); return false; }
      m.x += s.dir;
    }
  }
  // Vague électrique: over Robotman's electric carcass, the discharge runs through the whole trail.
  if (!s.electric) {
    for (const st of state.steles) {
      if (st.kind !== ROBOTMAN || st.stage >= LEGACY_TURNS || Math.abs(st.x - nx) > SURGE_BODY || st.y < floor - s.height - 2) continue;
      s.electric = true;
      // Through the trail (water cells) and the wave itself: the units it carries are in it.
      const back = Math.min(nx, nx - SURGE_BODY * s.dir);
      const front = Math.max(nx, nx - SURGE_BODY * s.dir);
      discharge(state, st.x, st.y - 2, 6, DISCHARGE_DAMAGE, (u) => {
        const ux = u.x >> FP_SHIFT;
        const uy = u.y >> FP_SHIFT;
        return ux <= front && ux + UNIT_W > back && uy + UNIT_H > floor - s.height - CREST_MARGIN && uy < floor;
      });
      break;
    }
  }

  s.x = nx;
  s.floor = floor;
  s.travelled++;
  if (s.travelled % SURGE_DECAY === 0) s.height--;
  return s.height > 0;
}

export function updateSurges(state: GameState): void {
  for (let i = state.surges.length - 1; i >= 0; i--) {
    const s = state.surges[i];
    if (s.eaten) {
      // Swallowed by a Roomba: all of it goes down its mouth, nothing is left on the ground.
      s.height -= SURGE_EATEN;
      if (s.height <= 0) state.surges.splice(i, 1);
      continue;
    }
    let alive = true;
    state.source = ownerPlayer(state, s.owner); // the wave's team (friendly fire)
    for (let k = 0; k < SURGE_SPEED && alive; k++) alive = advance(state, s);
    state.source = -1;
    // GDD (the Moon): with no air, the Wave freezes after 2 s and leaves a wall of ice.
    if (alive && state.map.airless && s.travelled >= SURGE_AIRLESS_COLUMNS) { freezeSurge(state, s); continue; }
    if (!alive && state.surges[i] === s) {
      breakSurge(state, s);
      state.surges.splice(i, 1);
    }
  }
}

/** Centre of the wave's front, for the camera. */
export function surgeCenter(s: Surge): [number, number] {
  return [s.x, s.floor - (s.height >> 1)];
}

